import { finalPdfPollMs } from "@shared/final-pdf.ts";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  useChildMatches,
  useNavigate,
} from "@tanstack/react-router";
import { useState } from "react";
import {
  Card,
  EmptyState,
  FILL_BODY,
  FILL_CARD,
  FILL_PAGE,
  PageHeader,
  Pagination,
  ROW_SCROLL,
  STICKY_THEAD,
  usePageSize,
} from "../components/account-ui.tsx";
import { OrderStatusBadge } from "../components/status.tsx";
import { ChevronRightIcon, ShoppingBagIcon } from "../icons/index.tsx";
import { api, type Campaign, type Order, type Product } from "../lib/api.ts";
import { formatDate, formatNumbers, formatPrices } from "../lib/legacy.ts";

export const Route = createFileRoute("/account/orders")({ component: PurchaseHistory });

export function useOrdersWithProducts() {
  const orders = useQuery({
    queryKey: ["orders"],
    queryFn: () => api.get<{ orders: Order[] }>("/orders"),
  });
  const campaigns = useQuery({
    queryKey: ["campaigns", "all"],
    queryFn: () => api.get<{ campaigns: Campaign[] }>("/campaigns?includeDeleted=1"),
    refetchInterval: (query) => finalPdfPollMs(query.state.data?.campaigns),
  });
  const catalog = useQuery({
    queryKey: ["catalog"],
    queryFn: () => api.get<{ products: Product[] }>("/catalog/products"),
  });
  const productFor = (o: Order): Product | undefined => {
    const c = campaigns.data?.campaigns.find((c) => c.id === o.campaign_id);
    return catalog.data?.products.find((p) => p.id === c?.product_id);
  };
  const campaignFor = (o: Order) => campaigns.data?.campaigns.find((c) => c.id === o.campaign_id);
  return {
    orders: orders.data?.orders,
    campaigns: campaigns.data?.campaigns,
    productFor,
    campaignFor,
    ready: orders.isSuccess,
  };
}

function PurchaseHistory() {
  const navigate = useNavigate();
  const [perPage, setPerPage] = usePageSize();
  const listKey = String(perPage);
  const [paged, setPaged] = useState({ key: listKey, n: 0 });
  // /account/orders/$code nests under this route, so the list has to stand
  // aside for it; without the Outlet an order detail never reaches the page.
  const childMatches = useChildMatches();
  const { orders, productFor, campaignFor, ready } = useOrdersWithProducts();
  if (childMatches.length > 0) return <Outlet />;
  if (!ready) return <div className={FILL_PAGE} />;

  const rows = [...(orders ?? [])].sort(
    (a, b) => (b.placed_at ?? b.created_at) - (a.placed_at ?? a.created_at),
  );
  const page = Math.min(
    paged.key === listKey ? paged.n : 0,
    Math.max(0, Math.ceil(rows.length / perPage) - 1),
  );
  const shown = rows.slice(page * perPage, (page + 1) * perPage);

  return (
    <div className={FILL_PAGE}>
      <PageHeader
        title="Purchase history"
        subtitle={
          rows.length === 1 ? "1 order placed" : `${formatNumbers(rows.length)} orders placed`
        }
      />
      <Card className={FILL_CARD} bodyClass={FILL_BODY}>
        {rows.length === 0 ? (
          <EmptyState
            Icon={ShoppingBagIcon}
            title="No orders yet"
            message="Once you pay for an approved campaign it will appear here."
            action={
              <Link
                to="/app/collections/$slug"
                params={{ slug: "all-products" }}
                className="inline-flex items-center rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
              >
                Start a Campaign
              </Link>
            }
          />
        ) : (
          <>
            <div className={ROW_SCROLL}>
              <table className="w-full text-left text-sm">
                <thead className={STICKY_THEAD}>
                  <tr>
                    <th className="px-5 py-3 font-semibold">Order</th>
                    <th className="hidden px-5 py-3 font-semibold sm:table-cell">Placed</th>
                    <th className="hidden px-5 py-3 text-right font-semibold md:table-cell">
                      Quantity
                    </th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3 text-right font-semibold">Total</th>
                    <th className="w-10 px-5 py-3" aria-label="Open" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {shown.map((order) => {
                    const product = productFor(order);
                    const campaign = campaignFor(order);
                    return (
                      <tr
                        key={order.id}
                        className="cursor-pointer transition-colors hover:bg-gray-50"
                        onClick={() =>
                          navigate({ to: "/account/orders/$code", params: { code: order.code } })
                        }
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            {product?.image ? (
                              <img
                                src={product.image}
                                alt=""
                                width={44}
                                height={44}
                                className="h-11 w-11 shrink-0 rounded-lg border border-gray-200 object-cover"
                              />
                            ) : (
                              <div className="h-11 w-11 shrink-0 rounded-lg bg-gray-100" />
                            )}
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900">{order.code}</p>
                              <p className="truncate text-xs text-gray-500">
                                {campaign?.name || product?.name || "Campaign"}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="hidden whitespace-nowrap px-5 py-4 text-gray-600 sm:table-cell">
                          {formatDate(order.placed_at ?? order.created_at)}
                        </td>
                        <td className="hidden px-5 py-4 text-right tabular-nums text-gray-600 md:table-cell">
                          {formatNumbers(order.quantity) || "0"}
                        </td>
                        <td className="px-5 py-4">
                          <OrderStatusBadge status={order.status} />
                        </td>
                        <td className="whitespace-nowrap px-5 py-4 text-right font-semibold tabular-nums text-gray-900">
                          ${formatPrices(order.total_cents / 100)}
                        </td>
                        <td className="px-5 py-4">
                          <ChevronRightIcon className="h-4 w-4 text-gray-300" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={perPage}
              total={rows.length}
              onPage={(n) => setPaged({ key: listKey, n })}
              onPageSize={setPerPage}
            />
          </>
        )}
      </Card>
    </div>
  );
}
